import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import type { DetectorConfig } from "../core/types.ts";

const WINDOW_SECONDS = 12;

/**
 * Live plot of the closure signal, raw against filtered, with the hysteresis
 * band drawn in.
 *
 * The gap between the two traces is the smoothing cost made visible — it is the
 * lag the filter is adding, in the place where it matters. Watching a blink edge
 * arrive late on the filtered trace tells you more about whether
 * blink-as-a-button will feel right than any single latency number does.
 *
 * uPlot rather than Chart.js: this redraws on every frame for the length of a
 * session, and uPlot does that without allocating per point.
 */
export class SignalChart {
  #plot: uPlot;
  #t: number[] = [];
  #raw: number[] = [];
  #filtered: number[] = [];
  #capacity: number;

  constructor(container: HTMLElement, config: DetectorConfig, fps: number) {
    this.#capacity = Math.ceil(WINDOW_SECONDS * fps * 1.5);

    this.#plot = new uPlot(
      {
        width: container.clientWidth || 480,
        height: 190,
        cursor: { show: false },
        legend: { show: true },
        scales: {
          x: { time: false },
          y: { range: [0, 1.02] },
        },
        axes: [
          {
            stroke: "#7d8792",
            grid: { stroke: "#232a32", width: 1 },
            ticks: { stroke: "#232a32" },
            values: (_u, splits) => splits.map((v) => `${v.toFixed(0)}s`),
          },
          {
            stroke: "#7d8792",
            grid: { stroke: "#232a32", width: 1 },
            ticks: { stroke: "#232a32" },
          },
        ],
        series: [
          {},
          { label: "raw", stroke: "#3f4b58", width: 1 },
          { label: "filtered", stroke: "#4ade80", width: 1.75 },
        ],
        hooks: {
          draw: [(u) => this.#drawThresholds(u, config)],
        },
      },
      [[], [], []],
      container,
    );

    const observer = new ResizeObserver(() => {
      this.#plot.setSize({ width: container.clientWidth, height: 190 });
    });
    observer.observe(container);
  }

  push(t: number, raw: number, filtered: number): void {
    const seconds = t / 1000;
    this.#t.push(seconds);
    this.#raw.push(Number.isFinite(raw) ? raw : Number.NaN);
    this.#filtered.push(Number.isFinite(filtered) ? filtered : Number.NaN);

    while (this.#t.length > this.#capacity) {
      this.#t.shift();
      this.#raw.shift();
      this.#filtered.shift();
    }

    const last = this.#t[this.#t.length - 1] as number;
    this.#plot.setData([this.#t, this.#raw, this.#filtered], false);
    this.#plot.setScale("x", { min: Math.max(0, last - WINDOW_SECONDS), max: last });
  }

  reset(): void {
    this.#t = [];
    this.#raw = [];
    this.#filtered = [];
    this.#plot.setData([[], [], []]);
  }

  /** Redraws the threshold guides after a config change. */
  refresh(config: DetectorConfig, fps: number): void {
    this.#capacity = Math.ceil(WINDOW_SECONDS * fps * 1.5);
    this.#plot.hooks.draw = [(u) => this.#drawThresholds(u, config)];
    this.#plot.redraw();
  }

  #drawThresholds(u: uPlot, config: DetectorConfig): void {
    const ctx = u.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
    ctx.clip();

    const line = (value: number, colour: string, dash: number[]) => {
      const y = u.valToPos(value, "y", true);
      ctx.strokeStyle = colour;
      ctx.setLineDash(dash);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(u.bbox.left, y);
      ctx.lineTo(u.bbox.left + u.bbox.width, y);
      ctx.stroke();
    };

    line(config.closeThreshold, "#f87171", [4, 4]);
    line(config.openThreshold, "#60a5fa", [4, 4]);
    line(config.completeAmplitude, "#fbbf24", [1, 5]);
    ctx.restore();
  }
}
